export const ZONE_TYPE = {
  ENTRY: "entry-zone",
  SALES: "sales-zone",
  DAMAGE: "damage-zone",
  PICKED: "picked-zone",
  SORTING: "sorting-zone",
};

const ZONE_TYPE_ALIASES = {
  entry: ZONE_TYPE.ENTRY,
  "entry-zone": ZONE_TYPE.ENTRY,
  sales: ZONE_TYPE.SALES,
  sale: ZONE_TYPE.SALES,
  "sales-zone": ZONE_TYPE.SALES,
  "manual-sales-zone": ZONE_TYPE.SALES,
  "robotic-sales-zone": ZONE_TYPE.SALES,
  "sales-zone-manual": ZONE_TYPE.SALES,
  "sales-zone-robotic": ZONE_TYPE.SALES,
  damage: ZONE_TYPE.DAMAGE,
  damaged: ZONE_TYPE.DAMAGE,
  "damage-zone": ZONE_TYPE.DAMAGE,
  picked: ZONE_TYPE.PICKED,
  "picked-zone": ZONE_TYPE.PICKED,
  sorting: ZONE_TYPE.SORTING,
  "sorting-zone": ZONE_TYPE.SORTING,
  sortingzone: ZONE_TYPE.SORTING,
  sorting_zone: ZONE_TYPE.SORTING,
  "sorting zone": ZONE_TYPE.SORTING,
  "sort-zone": ZONE_TYPE.SORTING,
  sort_zone: ZONE_TYPE.SORTING,
  sort: ZONE_TYPE.SORTING,
};

// Cross-zone pairs accepted by the Inventory Movement API (appendTransferCaseUpdates).
// Same zone type is handled separately, like pgw-web.
const CROSS_TYPE_DESTINATIONS = {
  [ZONE_TYPE.ENTRY]: [ZONE_TYPE.SALES, ZONE_TYPE.DAMAGE],
  [ZONE_TYPE.SALES]: [ZONE_TYPE.DAMAGE],
  [ZONE_TYPE.DAMAGE]: [ZONE_TYPE.SALES, ZONE_TYPE.ENTRY],
  [ZONE_TYPE.PICKED]: [ZONE_TYPE.SALES],
};

export const INVENTORY_MOVEMENT_MAX_ATTEMPTS = 3;
export const INVENTORY_MOVEMENT_CONFLICT_RETRIES =
  INVENTORY_MOVEMENT_MAX_ATTEMPTS - 1;
const MOVEMENT_RETRY_BASE_MS = 250;

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const asId = (value) => (value == null ? "" : String(value));

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function zoneFieldsType(zoneOrSpot) {
  if (!zoneOrSpot || typeof zoneOrSpot !== "object") return "";
  return (
    zoneOrSpot.type ??
    zoneOrSpot.zoneType ??
    zoneOrSpot.kind ??
    zoneOrSpot.zoneKind ??
    zoneOrSpot.locationType ??
    ""
  );
}

function zoneLabel(zoneOrSpot) {
  return String(zoneOrSpot?.name ?? zoneOrSpot?.zoneName ?? "").trim();
}

export function zoneRecordId(zone) {
  if (!zone || typeof zone !== "object") return "";
  const id = zone.id ?? zone.zoneId;
  return id == null ? "" : String(id);
}

// manual/robotic sales zones are sales for movement rules.
export function normalizeZoneType(zoneType) {
  const raw = String(zoneType ?? "")
    .trim()
    .toLowerCase();
  if (
    raw === ZONE_TYPE.SALES ||
    raw.startsWith(`${ZONE_TYPE.SALES}-`) ||
    raw.endsWith(`-${ZONE_TYPE.SALES}`)
  ) {
    return ZONE_TYPE.SALES;
  }
  return ZONE_TYPE_ALIASES[raw] ?? raw;
}

export function asZoneList(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  for (const key of ["items", "data", "racks", "shelves", "spots"]) {
    if (Array.isArray(data[key])) return data[key];
  }
  return [];
}

export function inventorySpotId(zoneId, spot) {
  const key = String(spot?.rackShelfSpotId ?? spot?.spotId ?? "").trim();
  const zone = String(zoneId ?? "");
  if (!key) return "";
  if (!zone || key === zone || key.startsWith(`${zone}-`)) return key;
  return `${zone}-${key}`;
}

function isStagingSpotCoords(zoneId, spot) {
  const id = inventorySpotId(zoneId, spot);
  const zone = String(zoneId ?? "");
  const rest =
    zone && id.startsWith(`${zone}-`) ? id.slice(zone.length + 1) : id;
  const nums = rest
    .split("-")
    .map((part) => parseInt(part, 10))
    .filter((n) => Number.isFinite(n));
  return nums.length >= 3 && nums.slice(-3).every((n) => n === 1);
}

export function adjustmentFromSpotId(zoneId, spots) {
  const list = asZoneList(spots);
  const staging =
    list.find((spot) => isStagingSpotCoords(zoneId, spot)) || list[0];
  return inventorySpotId(zoneId, staging) || `${zoneId}-01-01-01`;
}

export function isSortingZone(zoneOrSpot) {
  if (!zoneOrSpot || typeof zoneOrSpot !== "object") return false;
  if (normalizeZoneType(zoneFieldsType(zoneOrSpot)) === ZONE_TYPE.SORTING) {
    return true;
  }
  const label = zoneLabel(zoneOrSpot);
  return label === "מיון" || label.includes("מיון");
}

export function findSortingZone(zones) {
  const list = asZoneList(zones);
  return (
    list.find(
      (zone) => normalizeZoneType(zoneFieldsType(zone)) === ZONE_TYPE.SORTING,
    ) ||
    list.find((zone) => {
      const label = zoneLabel(zone);
      return label === "מיון" || label.includes("מיון");
    }) ||
    null
  );
}

export function findEntryZone(zones) {
  const list = asZoneList(zones);
  const sortingId = zoneRecordId(findSortingZone(list));
  return (
    list.find(
      (zone) =>
        zoneRecordId(zone) !== sortingId &&
        normalizeZoneType(zoneFieldsType(zone)) === ZONE_TYPE.ENTRY,
    ) ||
    list.find((zone) => zoneLabel(zone) === "כניסה") ||
    null
  );
}

// Adjustment transfers leave the sorting area. Branches that only have an
// entry zone use that staging area instead.
export function findAdjustmentSourceZone(zones) {
  return findSortingZone(zones) || findEntryZone(zones);
}

export function getSpotId(spot) {
  if (!spot || typeof spot !== "object") return "";
  return asId(spot.spotId ?? spot.spot_id ?? spot.id);
}

export function getSpotLocationId(spot) {
  if (!spot || typeof spot !== "object") return "";
  return asId(spot.locationId ?? spot.location_id);
}

export function getBatchLocationId(batch) {
  if (!batch || typeof batch !== "object") return "";
  return asId(batch.locationId ?? batch.location_id);
}

export function getBatchId(batch) {
  if (!batch || typeof batch !== "object") return "";
  return asId(batch.id ?? batch.batchId ?? batch.batch_id);
}

export function isVirtualBatch(batchOrSpot) {
  if (!batchOrSpot || typeof batchOrSpot !== "object") return false;
  if (
    batchOrSpot.isVirtual === true ||
    batchOrSpot.is_virtual === true ||
    batchOrSpot.virtual === true ||
    batchOrSpot.isVirtualBatch === true
  ) {
    return true;
  }
  return (
    String(batchOrSpot.batchType ?? "")
      .trim()
      .toLowerCase() === "virtual"
  );
}

export function spotAllocatedQuantity(spot) {
  if (!spot || typeof spot !== "object") return 0;
  return Math.max(
    0,
    toNumber(
      spot.allocatedQuantity ??
        spot.allocated_quantity ??
        spot.reservedQuantity,
      0,
    ),
  );
}

/** Quantity that can leave this spot. Allocated units cannot be moved. */
export function unallocatedSpotQuantity(spot) {
  if (!spot || typeof spot !== "object") return 0;
  return Math.max(0, toNumber(spot.quantity, 0) - spotAllocatedQuantity(spot));
}

export function isSalesZoneType(zoneType) {
  return normalizeZoneType(zoneType) === ZONE_TYPE.SALES;
}

export function isSpotInEntryZone(zoneType) {
  return normalizeZoneType(zoneType) === ZONE_TYPE.ENTRY;
}

export function isSpotInDamageZone(zoneType) {
  return normalizeZoneType(zoneType) === ZONE_TYPE.DAMAGE;
}

export function isSpotInPickedZone(zoneType) {
  return normalizeZoneType(zoneType) === ZONE_TYPE.PICKED;
}

/** Sales-zone types are always for sale, even if the API still sends isForSale: false. */
export function isSpotForSale({ isForSale, type } = {}) {
  if (isSalesZoneType(type)) return true;
  return isForSale === true;
}

export function sellableClass(zoneOrSpot) {
  const forSale = isSpotForSale({
    isForSale: zoneOrSpot?.isForSale ?? zoneOrSpot?.sellable,
    type: zoneOrSpot?.type ?? zoneOrSpot?.zoneType,
  });
  return forSale ? "sellable" : "active";
}

function sellableFlagOf(value) {
  return typeof value === "boolean" ? value : undefined;
}

function activeClassOf(value) {
  const status = String(value ?? "")
    .trim()
    .toLowerCase();
  return status || undefined;
}

export function sameSellableActiveClass(from, to) {
  const fromSellable = sellableFlagOf(from?.isForSale ?? from?.sellable);
  const toSellable = sellableFlagOf(to?.isForSale ?? to?.sellable);
  if (
    fromSellable !== undefined &&
    toSellable !== undefined &&
    fromSellable !== toSellable
  ) {
    return false;
  }
  const fromActive = activeClassOf(from?.zoneStatus);
  const toActive = activeClassOf(to?.status);
  if (fromActive && toActive && fromActive !== toActive) return false;
  return true;
}

export function isCompatibleSellableClass(from, to) {
  return sameSellableActiveClass(from, to);
}

export function isSupportedCrossZoneMovement(fromZoneType, toZoneType) {
  const fromType = normalizeZoneType(fromZoneType);
  const toType = normalizeZoneType(toZoneType);
  if (!fromType || !toType) return false;
  return (CROSS_TYPE_DESTINATIONS[fromType] || []).includes(toType);
}

export function isSupportedDestinationZoneType(fromZoneType, toZoneType) {
  const fromType = normalizeZoneType(fromZoneType);
  const toType = normalizeZoneType(toZoneType);
  if (!fromType || !toType) return false;
  if (fromType === toType) return true;
  return isSupportedCrossZoneMovement(fromType, toType);
}

export function getAllowedDestinationZoneTypes(fromZoneType) {
  const fromType = normalizeZoneType(fromZoneType);
  if (!fromType) return [];
  return [fromType, ...(CROSS_TYPE_DESTINATIONS[fromType] || [])].filter(
    (type, index, list) => list.indexOf(type) === index,
  );
}

export function isSameZoneTransfer(fromSpot, toZoneOrSpot) {
  if (!fromSpot || !toZoneOrSpot) return false;
  const fromType = normalizeZoneType(fromSpot.zoneType ?? fromSpot.type);
  const toType = normalizeZoneType(toZoneOrSpot.zoneType ?? toZoneOrSpot.type);
  if (!fromType || fromType !== toType) return false;
  return sameSellableActiveClass(fromSpot, toZoneOrSpot);
}

export function isSameZoneDestination(from, zone) {
  const zoneId = zoneRecordId(zone);
  if (!zoneId) return false;
  if (asId(from?.zoneId) === zoneId) return true;
  const spotId = getSpotId(from);
  return Boolean(
    spotId && (spotId === zoneId || spotId.startsWith(`${zoneId}-`)),
  );
}

/** Mirrors pgw-web locationsMatch: spot, batch and destination must share a location. */
export function locationsMatch(from, toLocationId, batch) {
  const fromLocation = getSpotLocationId(from);
  const batchLocation =
    getBatchLocationId(batch) || asId(from?.batchLocationId) || fromLocation;
  const toLocation = asId(toLocationId).trim();
  if (!fromLocation || !toLocation) return true;
  if (fromLocation !== toLocation) return false;
  if (batchLocation && batchLocation !== toLocation) return false;
  return true;
}

export function isSameLocationMovement(fromSpot, toZoneOrSpot, batch) {
  const toLocation =
    getSpotLocationId(toZoneOrSpot) || asId(toZoneOrSpot?.locationId);
  return locationsMatch(fromSpot, toLocation, batch);
}

/** Only Sale → Damaged reduces product availability. Same-zone does not. */
export function movementDecrementsProductAvailability(
  fromZoneType,
  toZoneType,
) {
  const fromType = normalizeZoneType(fromZoneType);
  const toType = normalizeZoneType(toZoneType);
  if (!fromType || !toType) return false;
  if (fromType === toType) return false;
  return isSpotForSale({ type: fromType }) && isSpotInDamageZone(toType);
}

export function maxTransferQuantity(
  spot,
  destinationZoneType,
  productAvailability,
) {
  if (isVirtualBatch(spot)) return 0;
  const unallocated = unallocatedSpotQuantity(spot);
  if (unallocated <= 0) return 0;

  const availability =
    productAvailability ??
    (typeof spot?.productAvailability === "number"
      ? spot.productAvailability
      : undefined);

  if (
    movementDecrementsProductAvailability(
      spot?.zoneType,
      destinationZoneType,
    ) &&
    typeof availability === "number" &&
    Number.isFinite(availability)
  ) {
    return Math.max(0, Math.min(unallocated, availability));
  }

  return unallocated;
}

export function findZoneForSpot(zones, spot) {
  const list = asZoneList(zones);
  const zoneId = asId(spot?.zoneId).trim();
  if (zoneId) {
    const exact = list.find((zone) => zoneRecordId(zone) === zoneId);
    if (exact) return exact;
  }
  const spotId = getSpotId(spot).trim();
  if (!spotId) return undefined;
  let best;
  list.forEach((zone) => {
    const id = zoneRecordId(zone).trim();
    if (!id) return;
    if (spotId === id || spotId.startsWith(`${id}-`)) {
      if (!best || id.length > zoneRecordId(best).length) best = zone;
    }
  });
  return best;
}

export function attachZoneMeta(spot, zones, fallbackLocationId) {
  if (!spot || typeof spot !== "object") return spot;
  const zone = findZoneForSpot(zones, spot);
  return {
    ...spot,
    zoneId: spot.zoneId || zone?.id,
    locationId:
      spot.locationId ||
      spot.location_id ||
      zone?.locationId ||
      fallbackLocationId,
    zoneName: zone?.name ?? spot.zoneName,
    zoneType: zone?.type ?? spot.zoneType,
    isForSale: isSpotForSale({
      isForSale: zone?.isForSale ?? spot.isForSale,
      type: zone?.type ?? spot.zoneType,
    }),
    isRobotic: zone?.isRobotic ?? spot.isRobotic,
    zoneStatus: zone?.status ?? spot.zoneStatus,
  };
}

/**
 * Destinations the Inventory Movement API will accept.
 * Manual vs robotic is not a backend rule — only zone type / isForSale flags are.
 */
export function isCompatibleDestinationZone(from, zone, batch) {
  if (!from || !zone) return false;
  if (isSameZoneDestination(from, zone)) return true;
  if (!locationsMatch(from, zone.locationId ?? zone.location_id, batch)) {
    return false;
  }

  if (isSameZoneTransfer(from, zone)) return true;

  const fromEntry = isSpotInEntryZone(from.zoneType);
  const fromDamage = isSpotInDamageZone(from.zoneType);
  const fromPicked = isSpotInPickedZone(from.zoneType);
  const fromSale = isSpotForSale({
    isForSale: from.isForSale,
    type: from.zoneType,
  });
  const toSale = isSpotForSale({ isForSale: zone.isForSale, type: zone.type });
  const toEntry = isSpotInEntryZone(zone.type);
  const toDamage = isSpotInDamageZone(zone.type);

  if (fromEntry && toSale) return true;
  if (fromSale && toDamage) return true;
  if (fromEntry && toDamage) return true;
  if (fromDamage && toSale) return true;
  if (fromDamage && toEntry) return true;
  if (fromPicked && toSale) return true;
  return false;
}

export function canTransferInventorySpot(spot, { batch } = {}) {
  if (!spot || typeof spot !== "object") return false;
  if (isVirtualBatch(spot) || isVirtualBatch(batch)) return false;
  return unallocatedSpotQuantity(spot) > 0;
}

export function filterDestinationZones(zones, fromSpot, options = {}) {
  if (!Array.isArray(zones) || !fromSpot || fromSpot === -1) return [];
  const enriched = attachZoneMeta(fromSpot, zones, options.locationId);
  return zones.filter((zone) =>
    isCompatibleDestinationZone(enriched, zone, options.batch),
  );
}

export function destinationSpotId(spot) {
  return `${spot?.zoneId}-${spot?.rackShelfSpotId}`;
}

export function filterCompatibleDestinationSpots(spots, from, zoneId) {
  const fromId = getSpotId(from);
  return (spots || []).filter((spot) => {
    const id = spot?.zoneId
      ? destinationSpotId(spot)
      : `${zoneId}-${spot?.rackShelfSpotId}`;
    return !fromId || id !== fromId;
  });
}

export const SINGLE_CELL_AREA_TRANSFER_MESSAGE =
  "באזור זה יש תא אחד בלבד, לא ניתן להעביר הלאה.";

export const EMPTY_DESTINATION_AREA_TRANSFER_MESSAGE =
  "באזור זה אין תאים, לא ניתן להעביר אליו.";

/** The loaded spots are only the source cell — nowhere else to move within this area. */
export function isSingleCellAreaTransferBlocked(spots, from, zoneId) {
  if (!Array.isArray(spots) || spots.length !== 1) return false;
  return filterCompatibleDestinationSpots(spots, from, zoneId).length === 0;
}

export function destinationAreaTransferIssue({
  from,
  spots,
  zoneId,
  zoneSelected,
  loading,
  racksCount,
  shelvesCount,
  rackSelected,
  shelfSelected,
}) {
  if (!zoneSelected || loading) return null;
  if (from && isSingleCellAreaTransferBlocked(spots, from, zoneId)) {
    return "single-cell";
  }
  if (racksCount === 0) return "empty";
  if (rackSelected && (shelvesCount ?? 0) === 0) return "empty";
  if (shelfSelected && (spots?.length ?? 0) === 0) return "empty";
  return null;
}

export function destinationAreaTransferMessage(issue) {
  if (issue === "single-cell") return SINGLE_CELL_AREA_TRANSFER_MESSAGE;
  if (issue === "empty") return EMPTY_DESTINATION_AREA_TRANSFER_MESSAGE;
  return null;
}

export function isSameInventorySpot(fromSpotId, toSpotId) {
  const from = asId(fromSpotId);
  const to = asId(toSpotId);
  return Boolean(from && to && from === to);
}

export function movementValidationError({
  from,
  toSpotId,
  toLocationId,
  toZoneType,
  quantity,
  productAvailability,
  batch,
}) {
  if (isVirtualBatch(from) || isVirtualBatch(batch)) {
    return "לא ניתן להעביר מלאי מאצווה וירטואלית";
  }
  const to = asId(toSpotId).trim();
  if (!to) {
    return "יש לבחור או להזין מספר תא";
  }
  const fromSpotId = getSpotId(from);
  if (fromSpotId && fromSpotId === to) {
    return "לא ניתן להעביר מלאי לאותו תא";
  }
  if (!locationsMatch(from, toLocationId, batch)) {
    return "לא ניתן להעביר מלאי בין תחנות שונות";
  }
  const fromZoneId = asId(from?.zoneId);
  const sameZoneBySpot = Boolean(
    fromZoneId && (to === fromZoneId || to.startsWith(`${fromZoneId}-`)),
  );
  if (
    !sameZoneBySpot &&
    from?.zoneType &&
    toZoneType &&
    !isSupportedDestinationZoneType(from.zoneType, toZoneType)
  ) {
    return "סוג ההעברה שנבחר אינו נתמך";
  }
  if (!(quantity > 0)) {
    return "יש להזין כמות תקינה";
  }
  const max = maxTransferQuantity(from, toZoneType, productAvailability);
  if (quantity > max) {
    if (max <= 0) return "אין כמות פנויה להעברה מתא זה";
    if (
      movementDecrementsProductAvailability(from?.zoneType, toZoneType) &&
      typeof productAvailability === "number" &&
      quantity > productAvailability
    ) {
      return "הכמות להעברה עולה על זמינות המוצר";
    }
    return "לא ניתן להעביר יחידות מוקצות. הכמות המקסימלית להעברה היא הכמות הפנויה";
  }
  return null;
}

export function readProductAvailabilityQuantity(raw) {
  const data = raw?.data ?? raw;
  if (data == null || typeof data !== "object") return null;
  const n = toNumber(
    data.quantity ?? data.availableQuantity ?? data.available_quantity,
    NaN,
  );
  return Number.isFinite(n) ? n : null;
}

export function parseInventoryMovementResponseBody(data) {
  if (data == null) return "";
  if (typeof data === "string") return data;
  return (
    data.message ||
    data.Message ||
    data.error ||
    data.Error ||
    data.msgTitle ||
    ""
  );
}

export function inventoryMovementUserMessage(status, backendMessage = "") {
  const text = String(backendMessage || "");
  const lower = text.toLowerCase();

  if (status === 409) {
    return "המלאי השתנה במקביל. הנתונים רועננו — נסה שוב";
  }
  if (status >= 500) {
    return "שגיאת שרת בהעברת המלאי. נסה שוב מאוחר יותר";
  }

  if (/virtual/i.test(text)) {
    return "לא ניתן להעביר מלאי מאצווה וירטואלית";
  }
  if (/unallocated/i.test(text)) {
    return "לא ניתן להעביר יותר מהכמות הלא מוקצית במיקום זה";
  }
  if (/product availability/i.test(text)) {
    return "הכמות להעברה עולה על כמות המוצר הזמינה";
  }
  if (/inventorySpot of from/i.test(text)) {
    return "לא נמצא תא המקור להעברה";
  }
  if (/same/.test(lower) && /spot/.test(lower)) {
    return "לא ניתן להעביר מלאי לאותו תא";
  }
  if (/location/.test(lower) && /different|cross/.test(lower)) {
    return "לא ניתן להעביר מלאי בין מיקומים שונים";
  }
  if (/unsupported inventory movement/i.test(text)) {
    return "סוג העברה זה אינו נתמך";
  }
  if (text.trim()) return text;
  if (status === 400) {
    return "ההעברה נדחתה: בקשה לא תקינה או כלל עסקי לא מתקיים";
  }
  return "שגיאה בהעברת המלאי";
}

async function readMovementErrorMessage(response, fallback) {
  let text = "";
  try {
    text = parseInventoryMovementResponseBody(await response.json());
  } catch {
    text = "";
  }
  if (!text.trim()) return fallback;
  return inventoryMovementUserMessage(response.status, text);
}

/**
 * Same flow as pgw-web postInventoryMovement: 400 stops, 409 refreshes and
 * retries with backoff, 5xx stops. `send` performs the actual POST.
 */
export async function postInventoryMovement({
  send,
  batchId,
  quantity,
  from,
  to,
  refresh,
  maxAttempts = INVENTORY_MOVEMENT_MAX_ATTEMPTS,
}) {
  let lastStatus = 0;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const response = await send({ batchId, quantity, from, to });
    lastStatus = response.status;

    if (response.ok) return { ok: true };

    if (response.status === 400) {
      return {
        ok: false,
        status: 400,
        retryable: false,
        message: await readMovementErrorMessage(
          response,
          "ההעברה נדחתה: בקשה לא תקינה או כלל עסקי לא מתקיים",
        ),
      };
    }

    if (response.status === 409) {
      if (refresh) {
        try {
          await refresh();
        } catch {
          /* still retry the movement */
        }
      }
      if (attempt < maxAttempts) {
        await sleep(
          Math.min(MOVEMENT_RETRY_BASE_MS * 2 ** (attempt - 1), 2000),
        );
        continue;
      }
      return {
        ok: false,
        status: 409,
        retryable: true,
        message: inventoryMovementUserMessage(409),
      };
    }

    if (response.status >= 500) {
      return {
        ok: false,
        status: response.status,
        retryable: false,
        message: await readMovementErrorMessage(
          response,
          "שגיאת שרת בהעברת המלאי. נסה שוב מאוחר יותר",
        ),
      };
    }

    return {
      ok: false,
      status: response.status,
      retryable: false,
      message: await readMovementErrorMessage(
        response,
        `שגיאה בהעברת המלאי (${response.status})`,
      ),
    };
  }

  return {
    ok: false,
    status: lastStatus,
    retryable: false,
    message: "שגיאה בהעברת המלאי",
  };
}

export function findBatchForSpot(batches, spot) {
  if (!Array.isArray(batches) || !spot) return null;
  const spotBatchId = asId(spot.batchId ?? spot.batch_id);
  if (!spotBatchId) return null;
  return batches.find((batch) => getBatchId(batch) === spotBatchId) || null;
}
